import React, { useCallback, useRef, useState } from 'react';

import { getIsWorkflowPhased } from '@aline/common';
import { TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import {
  getFirstPhaseColumnId,
  getIsPausedOrCancelledCategory,
  getNeedsUnparkConfirm,
  getTriageColumnId,
} from 'src/components/workflow/phase-column-helpers';
import { Apollo } from 'src/gql/generated';
import { getIsSigningPhaseType } from 'src/workflow/helpers';

import { useMoveWorkflowToColumn } from '../useMoveWorkflowToColumn';
import {
  getIsColumnDndId,
  getIsWorkflowDndId,
  parseColumnIdFromDndId,
} from '../utils/board-dnd-ids';
import {
  kanbanKeyboardCoordinateGetter,
  SelectiveKeyboardSensor,
  SelectiveMouseSensor,
  SelectivePointerSensor,
} from '../utils/keyboardCoordinateGetter';
import { getTaskProgress } from '../workflow-card/task-progress';
import type { WorkflowCardWorkflow } from '../workflow-card/WorkflowCard';

function getIsWorkflowCardWorkflow(value: unknown): value is WorkflowCardWorkflow {
  return (
    value != null &&
    typeof value === 'object' &&
    'id' in value &&
    '__typename' in value &&
    'name' in value
  );
}

export function getWorkflowFromDragData(
  data: Record<string, unknown> | undefined
): WorkflowCardWorkflow | undefined {
  if (!data) return undefined;
  const workflow = data.workflow;
  if (!getIsWorkflowCardWorkflow(workflow)) return undefined;
  return workflow;
}

export interface UseBoardDragAndDropOptions {
  board: Apollo.GetBoardQuery['board'] | undefined;
  /** Ref to the scrollable board container for scroll position preservation */
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
}

interface PendingUnparkMove {
  targetColumn: {
    category: Apollo.ColumnCategory;
    color: string;
    id: string;
    name: string;
  };
  workflow: WorkflowCardWorkflow;
}

interface PendingDoneMove extends PendingUnparkMove {
  openTaskCount: number;
}

export function useBoardDragAndDrop({ board, scrollContainerRef }: UseBoardDragAndDropOptions) {
  const { isMoving, moveWorkflow } = useMoveWorkflowToColumn();
  const [startWorkflowWorkMutation, { loading: isStartingWork }] =
    Apollo.useStartWorkflowWorkMutation();
  const scrollPositionRef = useRef<{ x: number; y: number } | null>(null);
  const [activeWorkflow, setActiveWorkflow] = useState<WorkflowCardWorkflow | null>(null);
  const [pendingStartWorkWorkflowId, setPendingStartWorkWorkflowId] = useState<string | null>(null);
  const [startWorkConfirmOpen, setStartWorkConfirmOpen] = useState(false);
  const [pendingUnparkMove, setPendingUnparkMove] = useState<PendingUnparkMove | null>(null);
  const [pendingDoneMove, setPendingDoneMove] = useState<PendingDoneMove | null>(null);

  const sensors = useSensors(
    useSensor(SelectivePointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(SelectiveKeyboardSensor, {
      coordinateGetter: kanbanKeyboardCoordinateGetter,
    }),
    useSensor(SelectiveMouseSensor, {
      activationConstraint: { distance: 10 },
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const workflowData = getWorkflowFromDragData(event.active.data.current);
    if (workflowData) setActiveWorkflow(workflowData);
  }, []);

  const startWorkflowWork = useCallback(
    async (workflowId: string) => {
      await startWorkflowWorkMutation({
        refetchQueries: 'active',
        variables: { workflowId },
      });
    },
    [startWorkflowWorkMutation]
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveWorkflow(null);

      if (!over || !board) return;

      const activeId = active.id.toString();
      const overId = over.id.toString();
      if (!getIsWorkflowDndId(activeId)) return;

      const workflow = getWorkflowFromDragData(active.data.current);
      const overWorkflow = getWorkflowFromDragData(over.data.current);
      const targetColumnId = getIsColumnDndId(overId)
        ? parseColumnIdFromDndId(overId)
        : overWorkflow?.column?.id;
      const sourceColumnId = workflow?.column?.id;

      if (!workflow || !sourceColumnId || !targetColumnId || sourceColumnId === targetColumnId)
        return;

      const targetColumn = board.columns.find(column => column.id === targetColumnId);
      if (!targetColumn) return;

      const container = scrollContainerRef?.current;
      if (container) {
        scrollPositionRef.current = {
          x: container.scrollLeft,
          y: container.scrollTop,
        };
      }

      const restoreScroll = () => {
        if (container && scrollPositionRef.current) {
          requestAnimationFrame(() => {
            container.scrollLeft = scrollPositionRef.current?.x ?? 0;
            container.scrollTop = scrollPositionRef.current?.y ?? 0;
            scrollPositionRef.current = null;
          });
        }
      };

      const triageColumnId = getTriageColumnId(board.columns);
      const firstPhaseColumnId = getFirstPhaseColumnId(workflow.phases ?? []);
      // A phaseless card starts by being moved, so only a phased one asks to start first.
      const isUnstartedPhasedTriageDrag =
        !workflow.startedAt &&
        Boolean(triageColumnId) &&
        sourceColumnId === triageColumnId &&
        getIsWorkflowPhased(workflow.phases, getIsSigningPhaseType);

      if (isUnstartedPhasedTriageDrag) {
        if (targetColumnId === firstPhaseColumnId) {
          await startWorkflowWork(workflow.id);
          restoreScroll();
          return;
        }

        setPendingStartWorkWorkflowId(workflow.id);
        setStartWorkConfirmOpen(true);
        restoreScroll();
        return;
      }

      const nextColumn = {
        category: targetColumn.category,
        color: targetColumn.color,
        id: targetColumn.id,
        name: targetColumn.name,
      };
      const openTaskCount = getOpenTasksBlockingDone(workflow, targetColumn.category);
      if (openTaskCount) {
        setPendingDoneMove({ openTaskCount, targetColumn: nextColumn, workflow });
        restoreScroll();
        return;
      }
      if (getNeedsUnparkConfirm(workflow.column.category, targetColumn.category)) {
        setPendingUnparkMove({ targetColumn: nextColumn, workflow });
        restoreScroll();
        return;
      }

      await moveWorkflow({
        confirmUnpark: getIsPausedOrCancelledCategory(workflow.column.category),
        targetColumn: nextColumn,
        workflow,
      });
      restoreScroll();
    },
    [board, moveWorkflow, scrollContainerRef, startWorkflowWork]
  );

  const handleDragCancel = useCallback(() => {
    setActiveWorkflow(null);
    scrollPositionRef.current = null;
  }, []);

  const confirmStartWork = useCallback(async () => {
    if (!pendingStartWorkWorkflowId) return;
    await startWorkflowWork(pendingStartWorkWorkflowId);
    setPendingStartWorkWorkflowId(null);
    setStartWorkConfirmOpen(false);
  }, [pendingStartWorkWorkflowId, startWorkflowWork]);

  const cancelStartWork = useCallback(() => {
    setPendingStartWorkWorkflowId(null);
    setStartWorkConfirmOpen(false);
  }, []);

  const confirmUnparkMove = useCallback(async () => {
    if (!pendingUnparkMove) return;
    await moveWorkflow({
      confirmUnpark: true,
      targetColumn: pendingUnparkMove.targetColumn,
      workflow: pendingUnparkMove.workflow,
    });
    setPendingUnparkMove(null);
  }, [moveWorkflow, pendingUnparkMove]);

  const cancelUnparkMove = useCallback(() => {
    setPendingUnparkMove(null);
  }, []);

  const confirmDoneMove = useCallback(async () => {
    if (!pendingDoneMove) return;
    await moveWorkflow({
      confirmUnpark: getIsPausedOrCancelledCategory(pendingDoneMove.workflow.column.category),
      targetColumn: pendingDoneMove.targetColumn,
      workflow: pendingDoneMove.workflow,
    });
    setPendingDoneMove(null);
  }, [moveWorkflow, pendingDoneMove]);

  const cancelDoneMove = useCallback(() => setPendingDoneMove(null), []);

  return {
    activeWorkflow,
    cancelDoneMove,
    cancelStartWork,
    cancelUnparkMove,
    confirmDoneMove,
    confirmStartWork,
    confirmUnparkMove,
    handleDragCancel,
    handleDragEnd,
    handleDragStart,
    isDragDisabled: isMoving || isStartingWork,
    pendingDoneMove,
    pendingUnparkMove,
    sensors,
    startWorkConfirmOpen,
  };
}

interface StartWorkConfirmDialogProps {
  onCancel: () => void;
  onConfirm: () => Promise<void> | void;
  open: boolean;
}

export function StartWorkConfirmDialog({
  onCancel,
  onConfirm,
  open,
}: StartWorkConfirmDialogProps): JSX.Element {
  return (
    <ConfirmationDialog
      message="Starting work will move this workflow to the first phase's home column, not the column you dropped on."
      open={open}
      primaryText="Start work"
      title="Start work from Triage?"
      onClickPrimary={onConfirm}
      onClose={onCancel}
    />
  );
}

interface OpenTasksDoneConfirmDialogProps {
  onCancel: () => void;
  onConfirm: () => Promise<void>;
  pendingMove: Pick<PendingDoneMove, 'openTaskCount' | 'targetColumn'> | null;
}

export function OpenTasksDoneConfirmDialog({
  onCancel,
  onConfirm,
  pendingMove,
}: OpenTasksDoneConfirmDialogProps): JSX.Element | null {
  if (!pendingMove) return null;
  const { openTaskCount, targetColumn } = pendingMove;
  const tasksText = openTaskCount === 1 ? '1 task is' : `${openTaskCount} tasks are`;
  return (
    <ConfirmationDialog
      open
      message={`${tasksText} still open. Move to ${targetColumn.name} anyway?`}
      primaryText={`Move to ${targetColumn.name}`}
      title="Tasks still open"
      onClickPrimary={onConfirm}
      onClose={onCancel}
    />
  );
}

/**
 * A single-phase workflow never moves itself to Done, so dragging it there is
 * the owner's call. Warn when that would leave top-level tasks open.
 */
function getOpenTasksBlockingDone(
  workflow: WorkflowCardWorkflow,
  targetCategory: Apollo.ColumnCategory
): number {
  if (
    targetCategory !== Apollo.ColumnCategory.Done ||
    getIsWorkflowPhased(workflow.phases, getIsSigningPhaseType)
  )
    return 0;
  return getTaskProgress(workflow.tasks).open;
}
